import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionCodes } from '../../auth/permissions';
import { useToast } from '../../notifications';
import {
  createReminderMessageTemplate,
  listReminderMessageTemplates,
  updateReminderMessageTemplate,
  type ReminderMessageTemplate,
} from '../services/messageTemplateService';
import {
  listReminderSubjectParameters,
  listReminderSubjectTypes,
} from '../services/reminderEngineService';
import type { ReminderEngineSubjectParameter, ReminderEngineSubjectType } from '../types/ReminderEngine';
import { truncateTemplatePreview } from '../utils/messageTemplateDefaults';
import templateStyles from './ReminderTemplatesPage.module.css';
import sharedStyles from './reminderEngineShared.module.css';

type EditorMode = 'create' | 'edit';

type TemplateEditorState = {
  mode: EditorMode;
  id?: string;
  name: string;
  content: string;
};

const emptyEditor = (): TemplateEditorState => ({
  mode: 'create',
  name: '',
  content: '',
});

export const ReminderTemplatesPage: React.FC = () => {
  const { t } = useTranslation(['reminderEngine', 'common']);
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PermissionCodes.messageTemplatesManage)
    || hasPermission(PermissionCodes.remindersManage);

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [templates, setTemplates] = React.useState<ReminderMessageTemplate[]>([]);
  const [subjectTypes, setSubjectTypes] = React.useState<ReminderEngineSubjectType[]>([]);
  const [selectedSubjectType, setSelectedSubjectType] = React.useState('');
  const [subjectParameters, setSubjectParameters] = React.useState<ReminderEngineSubjectParameter[]>([]);
  const [parametersLoading, setParametersLoading] = React.useState(false);
  const [editor, setEditor] = React.useState<TemplateEditorState | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const loadTemplates = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [templateItems, subjectTypeItems] = await Promise.all([
        listReminderMessageTemplates(),
        listReminderSubjectTypes(),
      ]);
      setTemplates(templateItems);
      setSubjectTypes(subjectTypeItems);
      setSelectedSubjectType((current) => current || subjectTypeItems[0]?.subjectType || '');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('loadFailed'));
      setTemplates([]);
      setSubjectTypes([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  React.useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  React.useEffect(() => {
    if (!selectedSubjectType) {
      setSubjectParameters([]);
      return;
    }

    let cancelled = false;
    setParametersLoading(true);
    void listReminderSubjectParameters(selectedSubjectType)
      .then((parameters) => {
        if (!cancelled) {
          setSubjectParameters(parameters);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSubjectParameters([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setParametersLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedSubjectType]);

  const openCreate = () => {
    setSaveError(null);
    setEditor(emptyEditor());
  };

  const openEdit = (template: ReminderMessageTemplate) => {
    setSaveError(null);
    setEditor({
      mode: 'edit',
      id: template.id,
      name: template.name,
      content: template.content,
    });
  };

  const closeEditor = () => {
    setEditor(null);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!editor) {
      return;
    }

    const name = editor.name.trim();
    const content = editor.content.trim();
    if (!name || !content) {
      setSaveError(t('templateFieldsRequired'));
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      if (editor.mode === 'create') {
        await createReminderMessageTemplate(name, content);
        showToast(t('templateCreated'), 'success');
      } else if (editor.id) {
        await updateReminderMessageTemplate(editor.id, name, content);
        showToast(t('templateUpdated'), 'success');
      }
      closeEditor();
      await loadTemplates();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('actionFailed');
      setSaveError(message);
      showToast(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={sharedStyles.stack}>
      <div className={sharedStyles.headerRow}>
        <div>
          <h3 className={sharedStyles.sectionTitle}>{t('templatesTitle')}</h3>
          <p className={sharedStyles.pageIntro}>{t('templatesSubtitle')}</p>
        </div>
        {canManage ? (
          <button type="button" className="btn btn-primary btn-sm" onClick={openCreate} disabled={Boolean(editor)}>
            {t('createTemplate')}
          </button>
        ) : null}
      </div>

      <p className={templateStyles.variablesHint}>{t('templateVariablesHint')}</p>

      {subjectTypes.length > 0 ? (
        <section className={templateStyles.parametersPanel}>
          <div className={templateStyles.parametersHeader}>
            <div>
              <strong>{t('templateParametersTitle')}</strong>
              <p className={templateStyles.parametersHint}>{t('templateParametersHint')}</p>
            </div>
            <label className={templateStyles.subjectTypeLabel}>
              {t('subjectType')}
              <select
                className="text-input"
                value={selectedSubjectType}
                onChange={(event) => setSelectedSubjectType(event.target.value)}
                disabled={parametersLoading}
              >
                {subjectTypes.map((item) => (
                  <option key={item.subjectType} value={item.subjectType}>{item.label}</option>
                ))}
              </select>
            </label>
          </div>
          {parametersLoading ? (
            <Spinner />
          ) : subjectParameters.length > 0 ? (
            <>
              <ul className={templateStyles.parametersList}>
                {subjectParameters.map((parameter) => (
                  <li key={parameter.key}>
                    <code>{`{{ ${parameter.key} }}`}</code>
                    <span>{parameter.label}</span>
                    {parameter.example ? <em>{parameter.example}</em> : null}
                  </li>
                ))}
              </ul>
              {subjectParameters.some((parameter) => parameter.key === 'consultationFields') ? (
                <p className={templateStyles.parametersHint}>{t('consultationFieldsTemplateHint')}</p>
              ) : null}
            </>
          ) : (
            <p className={templateStyles.parametersHint}>{t('noTemplateParameters')}</p>
          )}
        </section>
      ) : null}

      {editor && canManage ? (
        <section className={sharedStyles.sectionCard}>
          <div className={templateStyles.editorHeader}>
            <h4>{editor.mode === 'create' ? t('createTemplate') : t('editTemplate')}</h4>
            <button type="button" className="btn btn-secondary btn-sm" onClick={closeEditor} disabled={saving}>
              {t('cancel', { ns: 'common' })}
            </button>
          </div>
          {saveError ? <p className={sharedStyles.error}>{saveError}</p> : null}
          <div className={templateStyles.editorGrid}>
            <label className={templateStyles.fieldLabel}>
              {t('templateName')}
              <input
                className="text-input"
                value={editor.name}
                onChange={(event) => setEditor((current) => current && { ...current, name: event.target.value })}
                disabled={saving}
                placeholder={t('templateNamePlaceholder')}
              />
            </label>
            <label className={templateStyles.fieldLabel}>
              {t('templateContent')}
              <textarea
                className={`text-input ${templateStyles.contentInput}`}
                value={editor.content}
                onChange={(event) => setEditor((current) => current && { ...current, content: event.target.value })}
                disabled={saving}
                rows={6}
                placeholder={t('templateContentPlaceholder')}
              />
            </label>
          </div>
          <p className={templateStyles.nameHint}>{t('templateNameHint')}</p>
          <div className={templateStyles.editorActions}>
            <button type="button" className="btn btn-primary" onClick={() => void handleSave()} disabled={saving}>
              {saving ? t('saving', { ns: 'common', defaultValue: 'Saving…' }) : t('save', { ns: 'common' })}
            </button>
          </div>
        </section>
      ) : null}

      {loading && <Spinner />}
      {error && <p className={sharedStyles.error}>{error}</p>}

      {!loading && !error && (
        <section className={sharedStyles.sectionCard}>
          {templates.length === 0 ? (
            <p className={sharedStyles.emptyState}>{t('noTemplates')}</p>
          ) : (
            <table className={sharedStyles.table}>
              <thead>
                <tr>
                  <th>{t('templateName')}</th>
                  <th>{t('templatePreview')}</th>
                  {canManage ? <th>{t('actions', { ns: 'common', defaultValue: 'Actions' })}</th> : null}
                </tr>
              </thead>
              <tbody>
                {templates.map((template) => (
                  <tr key={template.id}>
                    <td><code>{template.name}</code></td>
                    <td className={templateStyles.previewCell}>{truncateTemplatePreview(template.content, 160)}</td>
                    {canManage ? (
                      <td>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => openEdit(template)}>
                          {t('edit', { ns: 'common' })}
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      <p className={templateStyles.footerHint}>
        {t('templatesUsageHint')}{' '}
        <Link to="/reminder-engine/definitions">{t('definitionsTitle')}</Link>.
      </p>
    </div>
  );
};
